/**
 * SVG Parser Utility
 * Handles SVG fetching, color extraction, and color replacement
 */

// Common color keywords to ignore
const IGNORED_COLORS = new Set([
  'none',
  'transparent',
  'currentColor',
  'currentcolor',
  'inherit',
]);

// Normalize color to lowercase hex format when possible
function normalizeColor(color: string): string {
  const trimmed = color.trim().toLowerCase();

  // Already hex
  if (trimmed.startsWith('#')) {
    // Expand shorthand hex (#abc -> #aabbcc)
    if (trimmed.length === 4) {
      const r = trimmed[1];
      const g = trimmed[2];
      const b = trimmed[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    return trimmed;
  }

  // RGB/RGBA - convert to hex
  const rgbMatch = trimmed.match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, '0');
    const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, '0');
    const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }

  // Named colors - return as-is (common ones)
  return trimmed;
}

// Check if a string looks like a color value
function isColorValue(value: string): boolean {
  const trimmed = value.trim().toLowerCase();

  if (IGNORED_COLORS.has(trimmed)) {
    return false;
  }

  // Hex color
  if (/^#[0-9a-f]{3,8}$/i.test(trimmed)) {
    return true;
  }

  // RGB/RGBA
  if (/^rgba?\s*\(/.test(trimmed)) {
    return true;
  }

  // Common named colors
  const namedColors = [
    'black', 'white', 'red', 'green', 'blue', 'yellow', 'cyan', 'magenta',
    'gray', 'grey', 'orange', 'purple', 'pink', 'brown', 'navy', 'teal',
    'olive', 'maroon', 'lime', 'aqua', 'fuchsia', 'silver', 'gold',
  ];

  return namedColors.includes(trimmed);
}

/**
 * Extract all fill and stroke colors from SVG content
 */
export function extractColors(svgContent: string): string[] {
  const colors = new Set<string>();

  // Pattern for fill="color" and stroke="color" attributes
  const attrPattern = /(?:fill|stroke)\s*=\s*["']([^"']+)["']/gi;
  let match;

  while ((match = attrPattern.exec(svgContent)) !== null) {
    const value = match[1];
    if (isColorValue(value)) {
      colors.add(normalizeColor(value));
    }
  }

  // Pattern for fill: color and stroke: color in style attributes
  const stylePattern = /(?:fill|stroke)\s*:\s*([^;}"']+)/gi;

  while ((match = stylePattern.exec(svgContent)) !== null) {
    const value = match[1].trim();
    if (isColorValue(value)) {
      colors.add(normalizeColor(value));
    }
  }

  return Array.from(colors).sort();
}

/**
 * Apply color overrides to SVG content
 */
export function applyColorOverrides(
  svgContent: string,
  overrides: Record<string, string>
): string {
  if (Object.keys(overrides).length === 0) {
    return svgContent;
  }

  let result = svgContent;

  for (const [originalColor, newColor] of Object.entries(overrides)) {
    if (!newColor || originalColor === newColor) continue;

    const normalizedOriginal = normalizeColor(originalColor);

    // Escape special regex characters in the color
    const escapedColor = normalizedOriginal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Replace in fill="color" and stroke="color" attributes
    // Be careful to match whole color values only
    const attrPattern = new RegExp(
      `((?:fill|stroke)\\s*=\\s*["'])${escapedColor}(["'])`,
      'gi'
    );
    result = result.replace(attrPattern, `$1${newColor}$2`);

    // Replace in style properties
    const stylePattern = new RegExp(
      `((?:fill|stroke)\\s*:\\s*)${escapedColor}([;}"'])`,
      'gi'
    );
    result = result.replace(stylePattern, `$1${newColor}$2`);

    // Handle shorthand hex in the original (#abc matches #aabbcc)
    if (originalColor.length === 4 && originalColor.startsWith('#')) {
      const shorthand = originalColor.toLowerCase();
      const shortEscaped = shorthand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      const shortAttrPattern = new RegExp(
        `((?:fill|stroke)\\s*=\\s*["'])${shortEscaped}(["'])`,
        'gi'
      );
      result = result.replace(shortAttrPattern, `$1${newColor}$2`);

      const shortStylePattern = new RegExp(
        `((?:fill|stroke)\\s*:\\s*)${shortEscaped}([;}"'])`,
        'gi'
      );
      result = result.replace(shortStylePattern, `$1${newColor}$2`);
    }
  }

  return result;
}

/**
 * Check if a source is an SVG
 */
export function isSvgSource(src: string): boolean {
  // Check file extension
  if (src.toLowerCase().endsWith('.svg')) {
    return true;
  }

  // Check data URL MIME type
  if (src.startsWith('data:image/svg+xml')) {
    return true;
  }

  return false;
}

/**
 * Fetch and parse SVG from a URL or data URL
 */
export async function fetchAndParseSvg(src: string): Promise<{
  content: string;
  colors: string[];
} | null> {
  try {
    let svgContent: string;

    if (src.startsWith('data:image/svg+xml')) {
      // Handle data URL
      if (src.includes('base64,')) {
        // Base64 encoded
        const base64 = src.split('base64,')[1];
        svgContent = atob(base64);
      } else {
        // URL encoded or plain
        const data = src.split(',')[1];
        svgContent = decodeURIComponent(data);
      }
    } else {
      // Fetch from URL
      const response = await fetch(src);
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.statusText}`);
      }
      svgContent = await response.text();
    }

    // Basic validation - check if it looks like SVG
    if (!svgContent.includes('<svg') && !svgContent.includes('<SVG')) {
      console.warn('Content does not appear to be SVG:', src);
      return null;
    }

    // Extract colors
    const colors = extractColors(svgContent);

    return {
      content: svgContent,
      colors,
    };
  } catch (error) {
    console.error('Error parsing SVG:', error);
    return null;
  }
}

/**
 * Build CSS filter string from filter values
 */
export interface FilterValues {
  brightness?: number; // percentage, default 100
  contrast?: number; // percentage, default 100
  saturate?: number; // percentage, default 100
  hueRotate?: number; // degrees, default 0
  blur?: number; // pixels, default 0
}

export function buildFilterString(filters: FilterValues): string {
  const parts: string[] = [];

  if (filters.brightness !== undefined && filters.brightness !== 100) {
    parts.push(`brightness(${filters.brightness}%)`);
  }
  if (filters.contrast !== undefined && filters.contrast !== 100) {
    parts.push(`contrast(${filters.contrast}%)`);
  }
  if (filters.saturate !== undefined && filters.saturate !== 100) {
    parts.push(`saturate(${filters.saturate}%)`);
  }
  if (filters.hueRotate !== undefined && filters.hueRotate !== 0) {
    parts.push(`hue-rotate(${filters.hueRotate}deg)`);
  }
  if (filters.blur !== undefined && filters.blur !== 0) {
    parts.push(`blur(${filters.blur}px)`);
  }

  return parts.join(' ');
}

/**
 * Parse CSS filter string to filter values
 */
export function parseFilterString(filter: string | undefined): FilterValues {
  const values: FilterValues = {
    brightness: 100,
    contrast: 100,
    saturate: 100,
    hueRotate: 0,
    blur: 0,
  };

  if (!filter) return values;

  const brightnessMatch = filter.match(/brightness\((\d+(?:\.\d+)?)%?\)/);
  if (brightnessMatch) {
    values.brightness = parseFloat(brightnessMatch[1]);
  }

  const contrastMatch = filter.match(/contrast\((\d+(?:\.\d+)?)%?\)/);
  if (contrastMatch) {
    values.contrast = parseFloat(contrastMatch[1]);
  }

  const saturateMatch = filter.match(/saturate\((\d+(?:\.\d+)?)%?\)/);
  if (saturateMatch) {
    values.saturate = parseFloat(saturateMatch[1]);
  }

  const hueMatch = filter.match(/hue-rotate\((-?\d+(?:\.\d+)?)deg\)/);
  if (hueMatch) {
    values.hueRotate = parseFloat(hueMatch[1]);
  }

  const blurMatch = filter.match(/blur\((\d+(?:\.\d+)?)px\)/);
  if (blurMatch) {
    values.blur = parseFloat(blurMatch[1]);
  }

  return values;
}
